import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Sidebar, Header } from '@/components';
import { useAuthStore } from '@/store/useAuthStore';
import { useTeamContext } from '@/hooks/useTeamContext';
import { useLocation } from 'react-router-dom';
import { useCurrentUser } from '@/hooks/queries/useCurrentUser';
import PropTypes from 'prop-types';
import AdminBanner from './AdminBanner';
import NetworkStatusBanner from './NetworkStatusBanner';
import ImpersonationBanner from './ImpersonationBanner';
import KycBanner from './KycBanner';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { useQueryClient } from '@tanstack/react-query';
import { posthog } from '@/lib/posthog';
import { useApiReady } from '@/hooks/useApiReady';
import { prefetchSidebarRoute } from '@/lib/navigation/prefetchSidebarRoute';
import { useSyncLocale } from '@/hooks/useSyncLocale';

const AppLayout = ({ children }) => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const setProfile = useAuthStore((state) => state.setProfile);
  const { user, activeTeamId, effectiveRole } = useTeamContext();

  useSyncLocale();

  const apiReady = useApiReady();

  // Navigation client : prefetch si pas déjà lancé au bootstrap session (reload F5).
  useLayoutEffect(() => {
    if (!apiReady || !effectiveRole) return;
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments.length === 0) return;
    void prefetchSidebarRoute({
      path: `/${segments[0]}`,
      role: effectiveRole,
      activeTeamId,
    });
  }, [apiReady, location.pathname, effectiveRole, activeTeamId]);
  const lastUserSnapshotRef = useRef(null);
  const isMountedRef = useRef(true);
  const mainScrollRef = useRef(null);

  useLayoutEffect(() => {
    const el = mainScrollRef.current;
    if (el) {
      el.scrollTop = 0;
    }
  }, [location.pathname]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    try {
      posthog.identify(user.id, {
        email: user.email ?? undefined,
        first_name: user.first_name ?? undefined,
        last_name: user.last_name ?? undefined,
      });
    } catch {
      // PostHog non initialisé ou erreur non bloquante
    }
  }, [user?.id, user?.email, user?.first_name, user?.last_name]);

  const { data: currentUserData } = useCurrentUser();

  // Sync shallow vers Zustand (bootstrap/guards) — lecture complète via useCurrentUser
  useEffect(() => {
    if (!currentUserData?.user || !isMountedRef.current) return;

    const newUser = currentUserData.user;
    const prev = lastUserSnapshotRef.current;
    const changed =
      !prev ||
      prev.id !== newUser.id ||
      prev.role !== newUser.role ||
      prev.avatar_path !== newUser.avatar_path ||
      prev.preferred_locale !== newUser.preferred_locale;

    if (changed) {
      lastUserSnapshotRef.current = {
        id: newUser.id,
        role: newUser.role,
        avatar_path: newUser.avatar_path,
        preferred_locale: newUser.preferred_locale,
      };
      setProfile(newUser);
    }
  }, [currentUserData, setProfile]);

  const authByAdmin = useMemo(() => {
    if (user && user.is_admin_login === true) return true;
    return false;
  }, [user]);

  return (
    <SidebarProvider className="relative h-screen flex flex-col overflow-x-clip max-w-full">
      <NetworkStatusBanner />
      <ImpersonationBanner />
      {authByAdmin && <AdminBanner />}
      <div className="flex-1 flex overflow-hidden min-h-0 min-w-0 max-w-full">
        <Sidebar />
        <SidebarInset className="flex flex-col min-h-0 min-w-0 max-w-full overflow-hidden">
          <Header />
          <KycBanner />
          <div
            ref={mainScrollRef}
            className="flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col overflow-x-hidden overflow-y-auto nav:overflow-hidden"
          >
            {children}
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

AppLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AppLayout;
